import { useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { scaleQuantize } from "d3-scale";

// GeoJSON 地图数据的URL - 使用GitHub上托管的加拿大省份边界数据
const geoUrl = "https://raw.githubusercontent.com/Mahshadn/canadian_map/master/canada_provinces.geojson";

// 模拟数据 - 加拿大各省的濒危物种数量和保护程度
const provinceData = [
  { id: "AB", name: "阿尔伯塔", species: 47, protection: 68 },
  { id: "BC", name: "不列颠哥伦比亚", species: 85, protection: 72 },
  { id: "MB", name: "曼尼托巴", species: 31, protection: 56 },
  { id: "NB", name: "新不伦瑞克", species: 22, protection: 61 },
  { id: "NL", name: "纽芬兰与拉布拉多", species: 18, protection: 55 },
  { id: "NS", name: "新斯科舍", species: 25, protection: 63 },
  { id: "NT", name: "西北领地", species: 15, protection: 70 },
  { id: "NU", name: "努纳武特", species: 12, protection: 65 },
  { id: "ON", name: "安大略", species: 65, protection: 59 },
  { id: "PE", name: "爱德华王子岛", species: 8, protection: 52 },
  { id: "QC", name: "魁北克", species: 57, protection: 64 },
  { id: "SK", name: "萨斯喀彻温", species: 35, protection: 58 },
  { id: "YT", name: "育空", species: 17, protection: 75 }
];

// 物种数量的颜色比例尺
const colorScale = scaleQuantize()
  .domain([0, 90])
  .range([
    "#e5f5e0",
    "#c7e9c0",
    "#a1d99b",
    "#74c476",
    "#41ab5d",
    "#238b45",
    "#006d2c",
    "#00441b"
  ]);

// 加拿大地图组件
const CanadaChoropleth = () => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [activeProvince, setActiveProvince] = useState(null);

  const handleMouseEnter = (geo, current) => {
    setTooltipContent(`
      <div>
        <h4>${current.name}</h4>
        <p>濒危物种数量: ${current.species}</p>
        <p>保护程度: ${current.protection}%</p>
      </div>
    `);
  };

  const handleMouseLeave = () => {
    setTooltipContent("");
  };

  const handleClick = (geo, current) => {
    setActiveProvince(current.id === activeProvince ? null : current.id);
  };

  // 查找省份数据 - 高级匹配逻辑
  const findProvinceData = (geoId) => {
    // 如果传入的geoId是空的
    if (!geoId) {
      console.log("警告: 收到空的geoId");
      return { name: "未知区域", species: 0, protection: 0 };
    }
    
    // 标准化ID格式（去掉空格，转为大写）
    const normalizedGeoId = geoId.toString().trim().toUpperCase();
    
    // 创建常见省份ID映射表
    const provinceMap = {
      // 英文名称与代码映射
      "ALBERTA": "AB", 
      "BRITISH COLUMBIA": "BC",
      "MANITOBA": "MB",
      "NEW BRUNSWICK": "NB",
      "NEWFOUNDLAND AND LABRADOR": "NL",
      "NEWFOUNDLAND": "NL", 
      "LABRADOR": "NL",
      "NOVA SCOTIA": "NS", 
      "NORTHWEST TERRITORIES": "NT",
      "NUNAVUT": "NU",
      "ONTARIO": "ON",
      "PRINCE EDWARD ISLAND": "PE",
      "QUEBEC": "QC", 
      "SASKATCHEWAN": "SK",
      "YUKON": "YT",
      "YUKON TERRITORY": "YT",
      // 中文名称与代码映射
      "阿尔伯塔": "AB", 
      "不列颠哥伦比亚": "BC",
      "曼尼托巴": "MB",
      "新不伦瑞克": "NB",
      "纽芬兰与拉布拉多": "NL",
      "新斯科舍": "NS", 
      "西北领地": "NT",
      "努纳武特": "NU",
      "安大略": "ON",
      "爱德华王子岛": "PE",
      "魁北克": "QC", 
      "萨斯喀彻温": "SK",
      "育空": "YT"
    };
    
    // 尝试直接匹配
    let province = provinceData.find(d => d.id === normalizedGeoId);
    
    // 如果没找到，尝试通过映射表匹配
    if (!province) {
      // 检查是否是省份名称，需要转换为省份代码
      let possibleCode = provinceMap[normalizedGeoId];
      if (possibleCode) {
        province = provinceData.find(d => d.id === possibleCode);
      } else {
        // 如果不是完整名称，尝试部分匹配
        for (const [name, code] of Object.entries(provinceMap)) {
          if (normalizedGeoId.includes(name) || name.includes(normalizedGeoId)) {
            province = provinceData.find(d => d.id === code);
            if (province) break;
          }
        }
      }
    }
    
    // 如果还是没找到，尝试更宽松的匹配
    if (!province) {
      for (const p of provinceData) {
        if (normalizedGeoId.includes(p.id) || p.id.includes(normalizedGeoId)) {
          province = p;
          break;
        }
        if (normalizedGeoId.includes(p.name) || p.name.includes(normalizedGeoId)) {
          province = p;
          break;
        }
      }
    }
    
    // 对于一些特定的ID映射
    if (!province) {
      // 省份代码可能使用数字
      const numericCodes = {
        '01': 'NL', '10': 'NL', 
        '11': 'PE', '12': 'NS', '13': 'NB', 
        '24': 'QC', '35': 'ON', 
        '46': 'MB', '47': 'SK', '48': 'AB', '59': 'BC',
        '60': 'YT', '61': 'NT', '62': 'NU'
      };
      
      if (numericCodes[normalizedGeoId]) {
        province = provinceData.find(d => d.id === numericCodes[normalizedGeoId]);
      }
    }
    
    console.log(`查找省份ID: ${geoId} => 找到: ${province ? province.name : '未找到'}`);
    return province || { name: "未识别区域", species: 0, protection: 0 };
  };

  return (
    <div className="flex flex-col items-center w-full h-full mx-auto max-w-4xl px-4">
      <h2 className="text-2xl font-bold mb-4">加拿大各省濒危物种分布图</h2>
      
      <div className="w-full">
        <ComposableMap
          projection="geoAzimuthalEqualArea"
          projectionConfig={{
            rotate: [100, -45, 0],
            scale: 600
          }}
          width={800}
          height={600}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "70vh"
          }}
        >
          <ZoomableGroup center={[-97, 60]} zoom={1}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map(geo => {
                  // 提取特定属性
                  const props = geo.properties;
                  // 尝试匹配各种可能的省份代码
                  const provinceCode = props.iso_a2 || props.code || props.PROVINCE || 
                                      props.abb || props.abbreviation || 
                                      props.GEO_CODE || props.P_CODE || props.prov_code;
                  
                  // 如果有名称，尝试从名称中提取代码
                  let provinceFromName = "";
                  if (props.name) {
                    const name = props.name.toString().toUpperCase();
                    if (name.includes("ALBERTA") || name === "AB") provinceFromName = "AB";
                    else if (name.includes("BRITISH") || name === "BC") provinceFromName = "BC";
                    else if (name.includes("MANITOBA") || name === "MB") provinceFromName = "MB";
                    else if (name.includes("NEW BRUNSWICK") || name === "NB") provinceFromName = "NB";
                    else if (name.includes("NEWFOUNDLAND") || name === "NL") provinceFromName = "NL";
                    else if (name.includes("NOVA SCOTIA") || name === "NS") provinceFromName = "NS";
                    else if (name.includes("NORTHWEST") || name === "NT") provinceFromName = "NT";
                    else if (name.includes("NUNAVUT") || name === "NU") provinceFromName = "NU";
                    else if (name.includes("ONTARIO") || name === "ON") provinceFromName = "ON";
                    else if (name.includes("PRINCE") || name === "PE") provinceFromName = "PE";
                    else if (name.includes("QUEBEC") || name === "QC") provinceFromName = "QC";
                    else if (name.includes("SASKATCHEWAN") || name === "SK") provinceFromName = "SK";
                    else if (name.includes("YUKON") || name === "YT") provinceFromName = "YT";
                  }
                                      
                  // 添加调试信息
                  console.log("省份属性:", props, "匹配到ID:", provinceCode || provinceFromName);
                  
                  // 尝试多种匹配方式
                  const current = findProvinceData(provinceCode || provinceFromName);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={colorScale(current.species)}
                      stroke="#FFFFFF"
                      strokeWidth={0.5}
                      style={{
                        default: {
                          outline: "none",
                          opacity: activeProvince ? (activeProvince === current.id ? 1 : 0.5) : 1
                        },
                        hover: { outline: "none", fill: "#FFD700", opacity: 1 },
                        pressed: { outline: "none", fill: "#FFD700", opacity: 1 }
                      }}
                      onMouseEnter={() => handleMouseEnter(geo, current)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleClick(geo, current)}
                      data-tooltip-id="province-tooltip"
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        
        <ReactTooltip
          id="province-tooltip"
          html={tooltipContent}
        />
      </div>
      <div className="mt-2 mb-2 flex justify-center items-center w-full">
        <div className="w-full max-w-md">
          <h3 className="text-xl font-bold text-center mb-1">图例</h3>
          <div className="flex items-center justify-center">
            <div className="text-sm mr-2">较少物种</div>
            <div className="flex border border-gray-300 rounded">
              {colorScale.range().map(color => (
                <div
                  key={color}
                  style={{
                    backgroundColor: color,
                    width: "1.5rem",
                    height: "0.75rem"
                  }}
                />
              ))}
            </div>
            <div className="text-sm ml-2">更多物种</div>
          </div>
        </div>
      </div>
      
      {activeProvince && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50 w-full max-w-md">
          <h3 className="text-xl font-bold mb-2">
            {findProvinceData(activeProvince).name} 详细数据
          </h3>
          <div className="space-y-4">
            <div>
              <p className="font-semibold">濒危物种数量: {findProvinceData(activeProvince).species}</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-green-600 h-2.5 rounded-full" 
                  style={{ width: `${findProvinceData(activeProvince).species / 90 * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <p className="font-semibold">保护程度: {findProvinceData(activeProvince).protection}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${findProvinceData(activeProvince).protection}%` }}
                ></div>
              </div>
            </div>
            <p className="text-sm italic">
              注: 数据为模拟数据，仅用于展示目的
            </p>
          </div>
        </div>
      )}
      
      <div className="mt-4 text-sm text-gray-500">
        <p>* 点击省份查看详细信息，拖动地图可以移动，滚轮可以缩放</p>
        <p>* 保护程度表示该省对濒危物种的保护措施评分</p>
      </div>
    </div>
  );
};

export default CanadaChoropleth;